import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Disc, Globe, Zap, Play, Pause } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import SectionHeading from "./SectionHeading";
import { useTr } from "@/i18n";

const genres = [
  {
    testid: "genre-tab-80s-90s",
    icon: Sparkles,
    title: "Anni 80/90",
    sub: "Revival Dance",
    desc: "I grandi classici che hanno fatto la storia della dance: da Gigi D'Agostino agli anthem intramontabili. In consolle passano Madonna, Spagna, Baltimora ed Eiffel 65: cori garantiti dal primo brano.",
    src: "/assets/mixes/euro-style.mp3",
  },
  {
    testid: "genre-tab-disco",
    icon: Disc,
    title: "Disco Dance",
    sub: "Nu-Disco & Funk",
    desc: "Groove senza tempo, bassline calde e glitter: la pista si accende con il sound disco più elegante. Da Earth, Wind & Fire ai Daft Punk, c'è il groove giusto per ogni generazione.",
    src: "/assets/mixes/disco.wav",
  },
  {
    testid: "genre-tab-afrohouse",
    icon: Globe,
    title: "Afro House",
    sub: "Deep & Tribal",
    desc: "Percussioni ipnotiche e atmosfere profonde: il suono che sta conquistando i club di tutto il mondo. Un viaggio tra radici africane ed elettronica moderna, perfetto al tramonto.",
    src: "/assets/mixes/afro-house.wav",
  },
  {
    testid: "genre-tab-techno",
    icon: Zap,
    title: "Techno",
    sub: "Melodic & Driving",
    desc: "Beat potenti e melodie avvolgenti per serate ad alta intensità fino a notte fonda. Dalla melodic techno dei festival europei alle sonorità più dure, sempre su misura per chi è in pista.",
    src: "/assets/mixes/techno.wav",
  },
];

export default function Genres() {
  const tr = useTr();
  const trGenres = tr("genres.items", genres);
  const items = genres.map((g, i) => ({ ...g, ...(trGenres[i] || {}) }));
  const [active, setActive] = useState(null);
  const audioRef = useRef(null);

  const toggle = (i) => {
    if (active === i) {
      audioRef.current?.pause();
      setActive(null);
      return;
    }
    window.dispatchEvent(new Event("dj-stop-audio"));
    audioRef.current?.pause();
    const audio = new Audio(items[i].src);
    audio.loop = true;
    audio.play().catch(() => setActive(null));
    audioRef.current = audio;
    setActive(i);
    trackEvent("genre_play", { genre: items[i].title });
  };

  useEffect(() => {
    const stop = () => {
      audioRef.current?.pause();
      setActive(null);
    };
    window.addEventListener("dj-stop-audio", stop);
    return () => {
      window.removeEventListener("dj-stop-audio", stop);
      audioRef.current?.pause();
    };
  }, []);

  return (
    <section id="generi" className="py-24 bg-[#0B0B0B]">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow={tr("genres.eyebrow", "Il Sound")}
          title={tr("genres.title", "I Generi del DJ Set")}
          accent={tr("genres.accent", "Quattro anime musicali, un'unica regia del dancefloor. Tocca un genere e ascolta l'anteprima.")}
          testid="genres-heading"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 px-6">
          {items.map((g, i) => {
            const isActive = active === i;
            return (
              <motion.div
                key={g.testid}
                data-testid={g.testid}
                role="button"
                tabIndex={0}
                aria-label={`Ascolta il mix ${g.title}`}
                onClick={() => toggle(i)}
                onKeyDown={(e) => e.key === "Enter" && toggle(i)}
                className={`gold-frame rounded-xl p-7 text-left bg-[#111111] cursor-pointer select-none ${
                  isActive ? "border-[rgba(247,222,139,0.7)] shadow-[0_0_45px_rgba(226,184,87,0.18)]" : ""
                }`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <g.icon size={28} className="text-[#E2B857]" strokeWidth={1.5} />
                  <div className="flex items-end gap-1 h-8" aria-hidden="true">
                    {[...Array(6)].map((_, b) => (
                      <span
                        key={b}
                        className={`w-1 h-full rounded-sm bg-gradient-to-t from-[#9E7B28] to-[#F7DE8B] ${
                          isActive ? "eq-bar" : "scale-y-[0.25] origin-bottom"
                        }`}
                        style={isActive ? { animationDelay: `${b * 0.09}s` } : undefined}
                      />
                    ))}
                  </div>
                </div>
                <h3 className="font-display text-lg font-semibold text-[#F7DE8B] mt-5">
                  {g.title}
                </h3>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#BFA45D] mt-1">
                  {g.sub}
                </p>
                <p className="text-sm text-[#E2C882]/80 leading-relaxed mt-3">{g.desc}</p>
                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-[rgba(226,184,87,0.18)]">
                  <span
                    data-testid={`audio-player-toggle-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ${
                      isActive
                        ? "gold-btn"
                        : "border border-[rgba(226,184,87,0.45)] text-[#E2B857]"
                    }`}
                  >
                    {isActive ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-[0.2em] ${
                      isActive ? "text-[#F7DE8B]" : "text-[#9F8A52]"
                    }`}
                  >
                    {isActive ? tr("genres.playing", "In riproduzione — tocca per fermare") : tr("genres.listen", "Ascolta il mix")}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
